import * as React from "react";
import { ChevronLeft, ChevronRight, ChevronDown, Maximize2 } from "lucide-react";
import { Highlight } from "@multiagentbook/ui";
import { useTranslation } from "react-i18next";
import TestimonialModal from "./TestimonialModal";

export interface Testimonial {
  id: string;
  quote: string;
  author: string;
  title: string;
  company?: string;
  avatar?: string;
  rating?: number;
  featured?: boolean;
  profileLink?: string;
  highlights?: string[]; // Array of exact phrases to highlight
  type?: "expert" | "reader" | "academic"; // Categorize testimonial source
}

interface TestimonialsProps {
  testimonials: Testimonial[];
  /** Use variant for i18n support: "experts" or "readers" */
  variant?: "experts" | "readers";
  /** @deprecated Use variant instead for i18n support */
  title?: string;
  /** @deprecated Use variant instead for i18n support */
  subtitle?: string;
  className?: string;
  layout?: "grid" | "masonry" | "carousel";
  itemsPerPage?: number;
  /**
   * Top divider + large spacing that lets the block stand alone as its own
   * section (used on the marketing site). Set false when a heading already
   * precedes the block, e.g. the service dashboard, to avoid a redundant gap.
   * Defaults to true to preserve existing standalone usage.
   */
  topDivider?: boolean;
}

const Testimonials: React.FC<TestimonialsProps> = ({
  testimonials,
  variant,
  title,
  subtitle,
  className = "",
  layout: propLayout,
  itemsPerPage: propItemsPerPage,
  topDivider = true,
}) => {
  const { t } = useTranslation("home");
  const [currentPage, setCurrentPage] = React.useState(0);
  const [isMobile, setIsMobile] = React.useState(false);
  const [selectedTestimonial, setSelectedTestimonial] =
    React.useState<Testimonial | null>(null);
  const sectionRef = React.useRef<HTMLElement>(null);

  // Detect mobile screen size
  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // Auto-configure layout and items based on screen size if not specified
  const layout = propLayout || (isMobile ? "grid" : "masonry");
  const itemsPerPage = propItemsPerPage || (isMobile ? 4 : 9);

  // Determine display subtitle - prefer variant-based i18n, fall back to props
  const displaySubtitle = React.useMemo(() => {
    if (variant === "experts") {
      return t("testimonials.expertsSubtitle", "What AI experts are saying about the book");
    }
    if (variant === "readers") {
      return t("testimonials.readersSubtitle", "What readers are saying");
    }
    // Fall back to prop or default
    return subtitle || t("testimonials.expertsSubtitle", "What AI experts are saying about the book");
  }, [variant, subtitle, t]);

  const totalPages = Math.ceil(testimonials.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTestimonials = testimonials.slice(startIndex, endIndex);

  const goToNextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const TestimonialCard: React.FC<{
    testimonial: Testimonial;
    onSelect: (testimonial: Testimonial) => void;
  }> = ({ testimonial, onSelect }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const maxLength = 450; // Character limit for truncation
    const shouldTruncate = testimonial.quote.length > maxLength;

    // Function to render text with highlights
    const renderHighlightedText = (text: string, highlights?: string[]) => {
      if (!highlights || highlights.length === 0) {
        return text;
      }

      // Create a regex pattern that matches all highlight phrases (case insensitive)
      const pattern = highlights
        .map((highlight) => highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")) // Escape special regex chars
        .join("|");

      const regex = new RegExp(`(${pattern})`, "gi");
      const parts = text.split(regex);

      return parts.map((part, index) => {
        const isHighlight = highlights.some(
          (highlight) => part.toLowerCase() === highlight.toLowerCase()
        );

        if (isHighlight) {
          return <Highlight key={index}>{part}</Highlight>;
        }
        return part;
      });
    };

    // Get the text to display (truncated or full)
    const getDisplayText = () => {
      if (!shouldTruncate || isExpanded) {
        return testimonial.quote;
      }

      // Find a good breaking point near the maxLength (prefer word boundaries)
      const truncated = testimonial.quote.substring(0, maxLength);
      const lastSpaceIndex = truncated.lastIndexOf(" ");
      const breakPoint =
        lastSpaceIndex > maxLength * 0.8 ? lastSpaceIndex : maxLength;

      return testimonial.quote.substring(0, breakPoint) + "...";
    };

    return (
      <div
        className={`
          bg-primary border border-secondary rounded-md p-4 sm:p-6
          hover:border-accent/20 transition-colors duration-200
          cursor-pointer group relative
          ${layout === "masonry" ? "break-inside-avoid mb-6" : ""}
        `}
        onClick={() => onSelect(testimonial)}
      >
        {/* Expand icon - shows on hover */}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Maximize2 className="w-4 h-4 text-tertiary" />
        </div>

        {/* Author info first - builds immediate trust */}
        <div className="mb-4">
          {/* Name prominently displayed */}
          <h3 className="font-bold text-primary text-lg leading-tight mb-1">
            {testimonial.author}
          </h3>

          {/* Title and company on same line */}
          <p className="text-base text-secondary font-medium">
            {testimonial.title}
            {testimonial.company && (
              <>
                {" at "}
                {testimonial.profileLink ? (
                  <a
                    href={testimonial.profileLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-accent hover:text-accent/80 transition-colors text-base"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {testimonial.company}
                  </a>
                ) : (
                  <span className="font-bold text-accent text-base">
                    {testimonial.company}
                  </span>
                )}
              </>
            )}
          </p>
        </div>

        {/* Quote section - with highlighting and expand/collapse */}
        <div className="relative">
          <div className="absolute -top-1 -left-1 text-accent/30">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 32 32">
              <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
            </svg>
          </div>

          <div className="pl-6">
            <p className="text-secondary leading-relaxed lg:text-base">
              {renderHighlightedText(getDisplayText(), testimonial.highlights)}
            </p>

            {/* Show more/less button */}
            {shouldTruncate && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                className="group inline-flex items-center gap-1 mt-3 text-xs font-medium text-accent hover:text-accent/80 transition-all duration-200"
                aria-expanded={isExpanded}
                aria-label={isExpanded ? "Show less" : "Show more"}
              >
                <span>{isExpanded ? t("testimonials.showLess", "Show less") : t("testimonials.showMore", "Show more")}</span>
                <ChevronDown
                  className={`w-3 h-3 transition-all duration-300 ease-in-out group-hover:scale-110 ${
                    isExpanded ? "rotate-180" : "rotate-0"
                  }`}
                />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={topDivider ? "border-t border-secondary mt-20 pt-20" : "mt-6"}>
      <div className="bg-primary">
        <section ref={sectionRef} className={`  ${className}`}>
          {/* Enhanced header with navigation */}
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            {displaySubtitle && (
              <p className="text-lg sm:text-xl text-secondary max-w-4xl mx-auto leading-relaxed mb-8">
                {displaySubtitle}
              </p>
            )}

            {/* Navigation controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <button
                  onClick={goToPrevPage}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary border border-tertiary rounded-lg hover:bg-accent hover:text-white transition-all duration-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="text-sm font-medium"> </span>
                </button>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-tertiary font-medium mr-1">
                    {currentPage + 1}/{totalPages}
                  </span>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        i === currentPage
                          ? "bg-accent"
                          : "bg-tertiary hover:bg-secondary"
                      }`}
                      aria-label={`Go to page ${i + 1}`}
                    />
                  ))}
                </div>

                <button
                  onClick={goToNextPage}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary border border-tertiary rounded-lg hover:bg-accent hover:text-white transition-all duration-300"
                >
                  <span className="text-sm font-medium"> </span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* True Masonry Layout with CSS columns for variable heights */}
          {layout === "masonry" ? (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-4 sm:gap-6">
              {currentTestimonials.map((testimonial) => (
                <TestimonialCard
                  key={testimonial.id}
                  testimonial={testimonial}
                  onSelect={setSelectedTestimonial}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {currentTestimonials.map((testimonial) => (
                <TestimonialCard
                  key={testimonial.id}
                  testimonial={testimonial}
                  onSelect={setSelectedTestimonial}
                />
              ))}
            </div>
          )}

          {/* Trust badge */}
          <div className="text-center mt-16">
            <div className="inline-flex items-center px-6 py-3 bg-accent/10 border border-accent/20 rounded-full hover:bg-accent/20 transition-all duration-300 group">
              <svg
                className="w-5 h-5 text-accent mr-3 group-hover:scale-110 transition-transform duration-300"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-accent font-semibold text-sm">
                Join 100s of readers building multi-agent systems
              </span>
            </div>
          </div>

          {/* Pagination controls - separate row */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-4">
              <button
                onClick={goToPrevPage}
                className="p-2 bg-secondary border border-tertiary rounded-lg hover:bg-accent hover:text-white transition-all duration-300"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <span className="text-secondary/60 text-sm">
                {startIndex + 1}-{Math.min(endIndex, testimonials.length)} of {testimonials.length}
              </span>

              <button
                onClick={goToNextPage}
                className="p-2 bg-secondary border border-tertiary rounded-lg hover:bg-accent hover:text-white transition-all duration-300"
                aria-label="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Simplified CSS - removed animations */}
          <style>{`
        /* CSS columns masonry layout */
        .columns-1 {
          column-count: 1;
          column-gap: 1rem;
        }

        @media (min-width: 640px) {
          .columns-1 {
            column-gap: 1.5rem;
          }
        }

        @media (min-width: 768px) {
          .md\\:columns-2 {
            column-count: 2;
          }
        }

        @media (min-width: 1024px) {
          .lg\\:columns-3 {
            column-count: 3;
          }
        }

        .break-inside-avoid {
          break-inside: avoid;
        }
      `}</style>
        </section>
      </div>

      {/* Testimonial Modal for screenshot-friendly view */}
      <TestimonialModal
        isOpen={selectedTestimonial !== null}
        onClose={() => setSelectedTestimonial(null)}
        testimonial={selectedTestimonial}
      />
    </div>
  );
};

export default Testimonials;
